import React from 'react';
import { useCart } from '../contexts/CartContext';
import { Button } from './ui/button';
import { ShoppingBag, Plus, Repeat, Trash2 } from 'lucide-react';

/**
 * Cart recovery modal — shown when a user logs in and they already had a
 * previous (server-side) cart saved on their account.
 *
 * The user is offered three explicit actions on their previous cart:
 *   1. Ajouter au panier actuel       (= merge)
 *   2. Remplacer le panier actuel     (= replace)
 *   3. Effacer le panier précédent    (= discard)
 *
 * The list of items from the previous cart is shown for context.
 */
const CartMergeModal = () => {
  const { pendingMerge, confirmMerge } = useCart();
  if (!pendingMerge) return null;

  const prev = pendingMerge.server || { items: [] };
  const items = prev.items || [];
  const totalQty = items.reduce((s, it) => s + (it.quantity || 0), 0);
  const totalAmount = items.reduce((s, it) => s + (it.daily_price || 0) * (it.quantity || 0), 0);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="cart-merge-modal"
    >
      <div className="bg-white max-w-xl w-full max-h-[90vh] flex flex-col border border-border shadow-2xl rounded-none">
        {/* Header */}
        <div className="p-5 border-b border-border flex items-start gap-4 flex-shrink-0">
          <div className="bg-primary/10 p-2.5 rounded-none flex-shrink-0">
            <ShoppingBag className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold">Vous aviez déjà un panier en cours</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Voici la liste des articles. Que souhaitez-vous faire ?
            </p>
          </div>
        </div>

        {/* Items list (read-only summary of the previous cart) */}
        <div className="overflow-y-auto flex-1 divide-y divide-border" data-testid="cart-merge-items">
          {items.length === 0 && (
            <p className="p-6 text-sm text-muted-foreground text-center">Aucun article.</p>
          )}
          {items.map((it, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 p-3"
              data-testid={`cart-merge-item-${idx}`}
            >
              {it.image_url ? (
                <img src={it.image_url} alt="" className="w-12 h-12 object-contain bg-white border border-border flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 bg-gray-100 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{it.product_name}</p>
                <div className="text-[11px] flex items-center gap-1.5 mt-0.5 flex-wrap">
                  {it.is_kit && <span className="bg-primary/10 text-primary px-1.5 py-0.5">Kit</span>}
                  {it.is_cross_sell && <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5">Cross-sell</span>}
                  {it.product_sku && <span className="text-muted-foreground font-mono">{it.product_sku}</span>}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-semibold">x{it.quantity}</p>
                {it.daily_price > 0 && (
                  <p className="text-[11px] text-muted-foreground">{it.daily_price.toFixed(2)} €</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Totals */}
        {items.length > 0 && (
          <div className="px-5 py-2 border-t border-border bg-gray-50 text-xs flex items-center justify-between flex-shrink-0">
            <span className="text-muted-foreground">{totalQty} article{totalQty > 1 ? 's' : ''}</span>
            <span className="font-semibold">{totalAmount.toFixed(2)} € / jour</span>
          </div>
        )}

        {/* Footer actions */}
        <div className="p-4 sm:p-5 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-2 flex-shrink-0">
          <Button
            className="rounded-none w-full whitespace-normal h-auto py-2.5 text-xs sm:text-sm leading-tight"
            onClick={() => confirmMerge('add')}
            data-testid="cart-merge-add"
          >
            <Plus className="w-4 h-4 mr-1.5 flex-shrink-0" /> Ajouter au panier actuel
          </Button>
          <Button
            variant="outline"
            className="rounded-none w-full whitespace-normal h-auto py-2.5 text-xs sm:text-sm leading-tight"
            onClick={() => confirmMerge('replace')}
            data-testid="cart-merge-replace"
          >
            <Repeat className="w-4 h-4 mr-1.5 flex-shrink-0" /> Remplacer le panier actuel
          </Button>
          <Button
            variant="outline"
            className="rounded-none w-full whitespace-normal h-auto py-2.5 text-xs sm:text-sm leading-tight border-red-300 text-red-700 hover:bg-red-50"
            onClick={() => confirmMerge('discard')}
            data-testid="cart-merge-discard"
          >
            <Trash2 className="w-4 h-4 mr-1.5 flex-shrink-0" /> Effacer le panier précédent
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CartMergeModal;
